import React from "react"

interface ColumnDef<T> {
  key: keyof T
  header: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (value: any, row: T) => React.ReactNode
  width?: string
  sortable?: boolean
}
interface IFooterCellData {
  content: string
  colSpan?: number
}
interface IFooterRowData {
  cellData: IFooterCellData[]
  className?: string
}
interface IFooterData {
  rowData: IFooterRowData[]
  className?: string
}

interface DataTableProps<T> {
  data: T[]
  columns: ColumnDef<T>[]
  loading?: boolean
  onSort?: (key: keyof T, direction: "asc" | "desc") => void
  sortColumn?: keyof T
  sortDirection?: "asc" | "desc"
  emptyMessage?: string
  footerData?: IFooterData
  renderSubRow?: (row: T) => React.ReactNode
  rowKey?: (row: T, index: number) => React.Key
  subRowClassName?: string
}

function CatchTable<T>({
  data,
  columns,
  loading = false,
  onSort,
  sortColumn,
  sortDirection,
  emptyMessage,
  footerData,
  renderSubRow,
  rowKey,
  subRowClassName,
}: DataTableProps<T>) {
  const handleSort = (column: ColumnDef<T>) => {
    if (!column.sortable || !onSort) return

    const newDirection = sortColumn === column.key && sortDirection === "asc" ? "desc" : "asc"

    onSort(column.key, newDirection)
  }

  const renderSortIcon = (column: ColumnDef<T>) => {
    if (!column.sortable) return null

    if (sortColumn !== column.key) {
      return <span className="ms-1">↕️</span>
    }

    return <span className="ms-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
  }

  const renderCell = (item: T, column: ColumnDef<T>) => {
    const value = item[column.key]
    if (column.formatter) {
      return column.formatter(value, item)
    }
    return value
  }

  if (loading) {
    return <div className="text-center p-3">Loading...</div>
  }

  if (!data?.length) {
    return <div className="text-center p-3">{emptyMessage || "No data available"}</div>
  }

  return (
    <div className="table-responsive">
      <table className="table table-centered table-nowrap">
        <thead className="table-light">
          <tr>
            {columns.map((column) => (
              <th
                key={String(column.key)}
                style={{ width: column.width }}
                onClick={() => handleSort(column)}
                className={column.sortable ? "cursor-pointer" : ""}
              >
                {column.header}
                {renderSortIcon(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            const subRow = renderSubRow?.(item)
            const resolvedRowKey = rowKey?.(item, index) ?? index

            return (
              <React.Fragment key={resolvedRowKey}>
                <tr>
                  {columns.map((column) => (
                    <td key={String(column.key)}>{renderCell(item, column)}</td>
                  ))}
                </tr>
                {subRow && (
                  <tr className={subRowClassName}>
                    <td colSpan={columns.length} className="p-0 border-0">
                      {subRow}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
        {!!footerData?.rowData.length && (
          <tfoot className={footerData.className}>
            {footerData?.rowData?.map((item, index) => (
              <tr key={index} className={item.className}>
                {item.cellData.map((cell, cellIndex) => (
                  <td key={cellIndex} colSpan={cell.colSpan ?? undefined}>
                    {cell.content}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  )
}

export default CatchTable
